import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { oneDark } from "@codemirror/theme-one-dark";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { java } from "@codemirror/lang-java";
import axios from "axios";
import "./QuestionEditor.css";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { INTERVIEWMANAGEMENT_URL } from "../../../Services/courseService";

const QuestionEditor = forwardRef(({
  questionNum,
  moveToNextQuestionCode,
  codingQuestionIndex,
  setCodingQuestionCount,
  studentId,
  handleCopyPasteCut,
  experienceLevel
}, ref) => {
  const { id, courseName } = useParams();

  const [question, setQuestion] = useState(null);
  const [code, setCode] = useState("");
  const [language, setLanguage] = useState("java");
  const [result, setResult] = useState(null);
  const [loadingQuestion, setLoadingQuestion] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(0);
  const [passed, setPassed] = useState(0);
  const [failed, setFailed] = useState(0);
  const userId = studentId;



  useEffect(() => {
    setLoadingQuestion(true);
    console.log(experienceLevel)
    axios
      .get(`${process.env.REACT_APP_API_BASE}/PROGRAMMINGSERVICE/api/questions/randomQuestion`, {
        params: {
          experienceLevel: experienceLevel
        }
      })
      .then((response) => {
        setQuestion(response.data);
        const defaultCode = response.data.defaultCodes?.[language] || "";
        setCode(defaultCode);
      })
      .catch((error) => console.error("Error fetching random question:", error))
      .finally(() => setLoadingQuestion(false));
  }, [language, questionNum]);


  const handleRun = async () => {
    if (!code.trim()) {
      toast.error("Code cannot be empty!");
      return;
    }

    if (!question?.questionId) {
      toast.error("Question ID not found!");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/PROGRAMMINGSERVICE/api/questions/runTestCases`,
        {
          questionId: question.questionId,
          code,
          language,
          studentId
        }
      );
      setResult(response.data);
    } catch (error) {
      console.error("Error submitting code:", error);
      setResult({ message: "Submission failed. Please try again.", testCaseResults: [] });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!question?.questionId) {
      toast.error("Question ID not found!");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/PROGRAMMINGSERVICE/api/questions/submit`,
        {
          questionId: question.questionId,
          code,
          language,
          studentId
        }
      );

      const newAttempted = attempted + 1;
      setAttempted(newAttempted);
      const submissionPassed = response.data?.passed; if (submissionPassed) {
        setPassed(prev => prev + 1);
      } else {
        setFailed(prev => prev + 1);
      }
      await updateProgrammingEvent(newAttempted, submissionPassed ? passed + 1 : passed, submissionPassed ? failed : failed + 1);

      setResult('');
      setCodingQuestionCount(prevCount => prevCount + 1);
    } catch (error) {
      console.error("Error submitting code:", error);
      setResult({ message: "Submission failed. Please try again.", testCaseResults: [] });
    } finally {
      setSubmitting(false);
    }
  };
  const interviewId = localStorage.getItem(`${id}-interviewId`);

  const updateProgrammingEvent = async (attempted, passed, failed) => {
    const payload = {
      interviewId,
      userId,
      status: "IN_PROGRESS-CODING",
      isProgrammingInterview: true,
      codingQuestionsAttempted: attempted,
      codingQuestionsPassed: passed,
      codingQuestionsFailed: failed,
    };
    try {
      await axios.post(`${INTERVIEWMANAGEMENT_URL}/v1/interviews/user/event`, payload);
    } catch (e) {
      console.error("Error updating programming event:", e);
    }
  };

  const handleSubmitContinue = () => {
    handleSubmit();
    moveToNextQuestionCode();

  }
  useImperativeHandle(ref, () => ({
    handleSubmit
  }));

  useEffect(() => {
    const blockEvent = (e) => {
      e.preventDefault();
    };

    document.addEventListener("copy", blockEvent);
    document.addEventListener("cut", blockEvent);
    document.addEventListener("paste", blockEvent);

    return () => {
      document.removeEventListener("copy", blockEvent);
      document.removeEventListener("cut", blockEvent);
      document.removeEventListener("paste", blockEvent);
    };
  }, []);
  useEffect(() => {
    const sendInitialEvent = async () => {
      const payload = {
        interviewId,
        userId,
        status: "IN_PROGRESS-CODING",
        isProgrammingInterview: true,
      };
      try {
        await axios.post(`${INTERVIEWMANAGEMENT_URL}/v1/interviews/user/event`, payload);
      } catch (e) {
        console.error("Error updating programming event on mount:", e);
      }
    };

    sendInitialEvent();
  }, []);


  return (
    <div className="coding-question-container">
      {loadingQuestion ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading Question...</p>
        </div>
      ) : question && (
        <div  >
          <p className="ai-interview-question">
            Coding section</p>
          <div className="question-content">


            <div className="question-section">
              <h2>{codingQuestionIndex}. {question?.title}</h2>
              <p>{question?.description}</p>
              <div className="question-test-cases">
                <h3>Sample Test Case</h3>
                <p><strong>Input:</strong> {question?.sampleInput}</p>
                <p><strong>Output:</strong> {question?.sampleOutput}</p>
              </div>
            </div>
            <div className="question-editor-section">
              <h3>Code Editor</h3>
              <CodeMirror
                value={code}
                height="400px"
                theme={oneDark}
                extensions={[
                  language === "javascript" ? javascript() :
                    language === "python" ? python() :
                      java()
                ]}
                onChange={(value) => setCode(value)}
                className="CodeMirror"
                onCopy={handleCopyPasteCut}
                onCut={handleCopyPasteCut}
                onPaste={handleCopyPasteCut}

              />

              <div className="question-controls">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={submitting}
                >
                  {/* <option value="javascript">JavaScript</option> */}
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                </select>
                <button onClick={handleRun} disabled={submitting} className="next-question-button"
                >  Run Code
                </button>
                <button onClick={handleSubmitContinue} disabled={submitting} className="next-question-button"
                >  Submit & continue
                </button>
              </div>
              {result?.testCaseResults?.length > 0 && (
                <div className="question-result">
                  <h3>Results</h3>
                  <p>{result?.message}</p>
                  <ul>
                    {result?.testCaseResults.map((test, index) => (
                      <li key={index} className={test.passed ? "passed" : "failed"}>
                        Test {index + 1}: {test.passed ? "✅ Passed" : "❌ Failed"}
                        <div className="test-details">
                          <p><strong>Input:</strong> {test.input}</p>
                          <p><strong>Expected Output:</strong> {test.expectedOutput}</p>
                          <p><strong>Received Output:</strong> {test.actualOutput}</p>
                        </div>
                      </li>
                    ))}
                  </ul>

                </div>
              )}
            </div>

          </div>

        </div>
      )}

    </div>
  );
});

export default QuestionEditor;